import PropTypes from "prop-types";
import React, { useEffect } from "react";
import withJobBoardContext from "../context";
import { formatDate } from "../../../lib/datetime_formatters";
import InquiryModal from "./InquiryModal";
import Button from "../../ui/Button";
import { formatCurrency } from "../../../lib/currency";
import MobileCard from "./MobileCard";
import useQueryParams from "../../../hooks/useQueryParams";

const Table = ({
  recycledInquiries,
  artistLoggedIn,
  artistServices,
  context,
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedInquiry, setSelectedInquiry] = React.useState(null);
  const {
    get: getQueryParam,
    set: setQueryParam,
    clear: clearQueryParam,
  } = useQueryParams();
  const selectedJobId = getQueryParam("jobId");

  const { selectedCountries, selectedCreativeFields, selectedOccasions } =
    context;
  const filteredRecycledInquiries = recycledInquiries
    .filter((inquiry) =>
      selectedCountries.includes(inquiry.country_code.toLowerCase()),
    )
    .filter((inquiry) => selectedCreativeFields.includes(inquiry.kind_of_art))
    .filter((inquiry) => selectedOccasions.includes(inquiry.kind_of_moment));

  const openModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setModalOpen(true);
  };

  const handleOpenModal = (inquiry) => {
    setQueryParam("jobId", inquiry.id);
    openModal(inquiry);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedInquiry(null);
  };

  const handleCloseModal = () => {
    clearQueryParam("jobId");
    closeModal();
  };

  const currentLocale = I18n.locale;

  useEffect(() => {
    if (selectedJobId) {
      const inquiry = recycledInquiries.find(
        (i) => i.id === Number(selectedJobId),
      );
      if (inquiry) {
        handleOpenModal(inquiry);
      }
    }
  }, [selectedJobId]);

  if (filteredRecycledInquiries.length === 0) {
    return (
      <div className="b-job-board__empty-state">
        <div style={{ color: "#666", fontSize: "16px" }}>
          {I18n.t("recycled_inquiries.index.empty_state.message")}
        </div>
        <div style={{ color: "#999", fontSize: "14px", marginTop: "8px" }}>
          {I18n.t("recycled_inquiries.index.empty_state.subtitle")}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="b-job-board__desktop-view">
        <table className="b-job-board__table">
          <thead>
            <tr>
              <th>{I18n.t("recycled_inquiries.index.table.inquiry_from")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.country")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.city")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.kind_of_art")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.service_type")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.occasion")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.event_date")}</th>
              <th>{I18n.t("recycled_inquiries.index.table.budget")}</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filteredRecycledInquiries.map((recycledInquiry) => {
              const {
                id,
                inquired_at,
                city,
                currency,
                country_code,
                budget_min,
                budget_max,
                kind_of_art,
                service_type,
                kind_of_moment,
                occasion_date,
                date,
              } = recycledInquiry;
              const countryCode =
                country_code !== undefined
                  ? country_code.toLowerCase()
                  : "none";
              const country = I18n.t(`country.${countryCode}`);
              let budget = "-";
              if (budget_min && budget_max) {
                budget = `${formatCurrency(
                  currentLocale,
                  currency,
                  budget_min,
                )} - ${formatCurrency(currentLocale, currency, budget_max)}`;
              } else if (budget_min) {
                budget = `${formatCurrency(
                  currentLocale,
                  currency,
                  budget_min,
                )}+`;
              } else if (budget_max) {
                budget = `0 - ${formatCurrency(
                  currentLocale,
                  currency,
                  budget_max,
                )}`;
              }
              return (
                <tr key={id}>
                  <td>{formatDate(inquired_at)}</td>
                  <td>{country}</td>
                  <td>{city}</td>
                  <td>
                    {kind_of_art
                      ? I18n.t(`categories.${kind_of_art}`, {
                          defaultValue: kind_of_art,
                        })
                      : "-"}
                  </td>
                  <td>
                    {service_type
                      ? I18n.t(`subtypes.${service_type}`, {
                          defaultValue: service_type,
                        })
                      : "-"}
                  </td>
                  <td>
                    {kind_of_moment
                      ? I18n.t(`kinds_of_moment.${kind_of_moment}`)
                      : "-"}
                  </td>
                  <td>
                    {formatDate(occasion_date) || formatDate(date) || "-"}
                  </td>
                  <td>{budget}</td>
                  <td>
                    <Button
                      classOverride="inline-button inline-button--orange"
                      onClick={() => handleOpenModal(recycledInquiry)}
                    >
                      {I18n.t(
                        "recycled_inquiries.index.inquiry_modal.see_inquiry",
                      )}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="b-job-board__mobile-view">
        <div className="b-job-board__mobile-cards">
          {filteredRecycledInquiries.map((recycledInquiry) => (
            <MobileCard
              key={recycledInquiry.id}
              recycledInquiry={recycledInquiry}
              onOpenModal={handleOpenModal}
              currentLocale={currentLocale}
            />
          ))}
        </div>
      </div>

      <InquiryModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        inquiry={selectedInquiry}
        isArtistLoggedIn={artistLoggedIn}
        artistServices={artistServices}
      />
    </>
  );
};

Table.propTypes = {
  recycledInquiries: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      inquired_at: PropTypes.string.isRequired,
      country: PropTypes.string.isRequired,
      city: PropTypes.string.isRequired,
      kind_of_art: PropTypes.string.isRequired,
      service_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      kind_of_moment: PropTypes.string.isRequired,
      occasion_time: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      budget_min: PropTypes.number.isRequired,
      budget_max: PropTypes.number.isRequired,
      currency: PropTypes.string.isRequired,
    }),
  ).isRequired,
  artistLoggedIn: PropTypes.bool,
  artistServices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      profileId: PropTypes.number,
      profileName: PropTypes.string,
    }),
  ),
  context: PropTypes.object.isRequired,
};

Table.defaultProps = {
  artistLoggedIn: false,
  artistServices: [],
};

export default withJobBoardContext(Table);
